import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/core';
import { formatDateOnly, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { LeaveSheetPolicy, LeaveSheetRow } from '../../types/leave-sheet.types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The weekday names are their own keys in `generic`. */
const WEEKDAY_KEYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Mon-DD-YYYY, Weekday" of a plain day, read by its UTC parts (kind A, never converted). */
const dateLabel = (day: string, t: Translate) => {
  const [year, month, date] = day.split('-').map(Number);
  const weekday = t(WEEKDAY_KEYS[new Date(Date.UTC(year, month - 1, date)).getUTCDay()]);
  return `${formatDateOnly(toDateOnlyValue(day))}, ${weekday}`;
};

/** The legacy sheet's note for a row. */
export const leaveSheetNoteOf = (row: LeaveSheetRow, t: Translate): string => {
  const half = row.isHalfDay ? ` (${t('HALF_DAY')})` : '';
  const toil = row.fromToil ? ` (${t('TOIL')})` : '';
  switch (row.kind) {
    case 'carryForward':
      return t('LEAVE_SHEET_NOTE_TOIL_CARRY_FORWARD');
    case 'toil':
      if (row.toilPool === 'SpecialToil')
        return `${t('LEAVE_SHEET_NOTE_EARNED_SPECIAL_TOIL')}${half}`;
      if (row.toilPool === 'WeekendToil')
        return `${t('LEAVE_SHEET_NOTE_EARNED_WEEKEND_TOIL')}${half}`;
      return `${t('LEAVE_SHEET_NOTE_EARNED_TOIL')}${half}`;
    case 'leave':
      return `${t('LEAVE_SHEET_NOTE_APPLIED_FOR', { title: row.title ?? '' })}${half}${row.fromSpecialToil ? ' (STL)' : ''}${toil}`;
    case 'missedAttendance':
      return `${t('LEAVE_SHEET_NOTE_DID_NOT_APPLY')}${half}${toil}`;
  }
};

const column = (
  id: string,
  title: string,
  value: (row: LeaveSheetRow) => string | number
): ColumnDef<LeaveSheetRow> => ({
  id,
  header: ({ column: col }) => <DataTableColumnHeader column={col} title={title} />,
  accessorFn: value,
  enableSorting: false,
  cell: ({ row }) => <span className="tabular-nums">{value(row.original)}</span>,
});

/**
 * The legacy sheet's columns, picked by the employee's `toilBalance` rule instead of their region:
 * no TOIL columns without one, one TOIL pool (earned, left) for the combined rule, and weekend and
 * special TOIL apart for the split rule.
 */
export const createLeaveSheetTableColumns = ({
  t,
  policy,
  defaultCategory,
}: {
  t: Translate;
  policy: LeaveSheetPolicy;
  /** The default leave's title, for the carry-forward row ("Annual Leave with TOIL CF"). */
  defaultCategory: string;
}): ColumnDef<LeaveSheetRow>[] => {
  const isEarning = (row: LeaveSheetRow) => row.kind === 'toil' || row.kind === 'carryForward';
  const dash = (days: number) => (days ? days : '-');

  return [
    {
      id: 'date',
      meta: { filter: { type: 'date' } },
      header: ({ column: col }) => <DataTableColumnHeader column={col} title={t('DATE')} />,
      accessorFn: (row) => row.date,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.kind === 'carryForward'
          ? t('LEAVE_SHEET_TOIL_CARRY_FORWARD_ROW', { category: defaultCategory })
          : dateLabel(row.original.date, t),
    },
    column('nos', t('NOS'), (row) => (isEarning(row) ? '' : row.leaveDays)),
    ...(policy.toil === 'combined'
      ? [column('toil', t('TOIL'), (row) => (isEarning(row) ? row.toilDays : '-'))]
      : []),
    ...(policy.toil === 'split'
      ? [
          column('weekendToilEarned', t('WEEKEND_TOIL_EARNED'), (row) =>
            row.kind === 'toil' && row.toilPool === 'WeekendToil' ? row.toilDays : dash(0)
          ),
          column('specialToilEarned', t('SPECIAL_TOIL_EARNED'), (row) =>
            row.kind === 'toil' && row.toilPool === 'SpecialToil' ? row.toilDays : dash(0)
          ),
          column('weekendToilLeft', t('WEEKEND_TOIL_REMAINING'), (row) => row.weekendToilLeft),
          column('specialToilLeft', t('SPECIAL_TOIL_REMAINING'), (row) => row.specialToilLeft),
        ]
      : []),
    // Card: the first value of the body.
    {
      ...column('balance', t('BALANCE'), (row) => row.balance),
      meta: { mobileOrder: 0 },
    },
    ...(policy.toil === 'combined'
      ? [column('toilLeft', t('TOIL_LEFT'), (row) => row.toilLeft)]
      : []),
    {
      id: 'note',
      // Card: in the footer, as in the old portal.
      meta: { filter: { type: 'text' }, mobile: 'footer' },
      header: ({ column: col }) => <DataTableColumnHeader column={col} title={t('NOTE')} />,
      accessorFn: (row) => leaveSheetNoteOf(row, t),
      enableSorting: false,
      cell: ({ row }) => leaveSheetNoteOf(row.original, t),
    },
  ];
};
